import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';
import { TvLoginStore } from '../../pages/app/services/app.store';
import { ClientSSO } from '../entities/client-sso';
import { LoginRequest } from '../entities/login-request';
import { LoginResponse } from '../entities/login-response';
import { EQueryStringParams, LocalStorageService } from '../stores/local-storage.service';
import { DictionaryAPIService } from './dictionary-api.service';
import { MobileService } from './mobile.service';

@Injectable({
  providedIn: 'root',
})
export class LoginAPIService {
  readonly tvLoginStore = inject(TvLoginStore);
  private cacheClientSSO: ClientSSO;

  constructor(
    private dictionaryAPI: DictionaryAPIService,
    private mobileService: MobileService,
    private http: HttpClient,
    private LocalStorageService: LocalStorageService
  ) {}

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error);
  }

  login(model: LoginRequest): Promise<LoginResponse> {
    return this.http
      .post(this.dictionaryAPI.login() + '?isMobile=' + (this.mobileService.isMobileApp() ? 'true' : 'false'), model)
      .toPromise()
      .then(async (response: LoginResponse) => {
        const client = await this.getClientExtraData(response.client);
        response.client = client;
        return response;
      })
      .catch(this.handleError);
  }

  setCSRFToken(): Promise<any> {
    return this.http
      .get(this.dictionaryAPI.csrfToken(), { responseType: 'text' })
      .toPromise()
      .then((token) => this.tvLoginStore.setCSRFToken(token));
  }

  checkInternetConnection(url: string): Promise<any> {
    return this.http.get(url).toPromise().catch(this.handleError);
  }

  getClientSSO(clientName: string): Promise<any> {
    const isTheSameClient = (alias) => alias === clientName;

    if (this.cacheClientSSO && isTheSameClient(this.cacheClientSSO.alias)) {
      return Promise.resolve(this.cacheClientSSO);
    }

    return this.http
      .get(
        this.dictionaryAPI.getClientSSO(clientName) +
          '&isMobile=' +
          (this.mobileService.isMobileApp() ? 'true' : 'false')
      )
      .toPromise()
      .then((response: ClientSSO) => {
        const returnUrl = this.LocalStorageService.getQueryParamFromLocalStorage(EQueryStringParams.returnUrl);

        if (returnUrl) {
          return this.http
            .post(this.dictionaryAPI.getSSORelayState(), { relaystate: returnUrl })
            .toPromise()
            .then(() => response);
        }

        return response;
      })
      .then((response: ClientSSO) => this.getClientExtraData(response))
      .then((response: ClientSSO) => {
        this.cacheClientSSO = isTheSameClient(response.alias) ? (this.cacheClientSSO = response) : null;
        return response;
      })
      .catch((response) => {
        this.cacheClientSSO = null;
        return Promise.reject(response);
      });
  }

  async getPortals(email: string): Promise<ClientSSO[]> {
    return firstValueFrom(this.http.get<ClientSSO[]>(this.dictionaryAPI.getPortals(), { params: { email } }));
  }

  private getClientExtraData(client: ClientSSO): Promise<any> {
    return Promise.all([
      this.http
        .get(this.dictionaryAPI.clientLanguages(client.id))
        .toPromise()
        .then((o: any[]) => o.map((i) => i.id)),
      this.http
        .get(this.dictionaryAPI.customization(client.id))
        .toPromise()
        .then((o: any) => o),
      this.http
        .get(this.dictionaryAPI.localization(client.id))
        .toPromise()
        .then((o: any) => o),
    ]).then(([languageIds, customization, nameTranslations]) => {
      return { ...client, languageIds, customization, nameTranslations };
    });
  }
}
